/**
 * SOLID Review
 * S — Strong. Sole job is defining the Result<T> success/failure discriminated union, the failure's wire shape, and the two
 *     constructors (ok/fail).
 * O — Strong. New call sites extend behavior by pattern-matching on `success`; a new failure code needs no change here.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Two tiny constructor functions plus the types themselves.
 * D — Strong. Zero imports — dependency-free, so every layer may import it.
 */

/**
 * THE FAILURE HALF OF A RESULT IS DATA (frontend-infrastructure-oo P3, 2026-10-29, §28.288 — the backend's since 2026-09-11, plan B2;
 * the owner's alternative A for the frontend, decided 2026-10-28, §28.277).
 *
 * It was `error: Error` until this change — the adapters' Result, which no port could carry: an `Error` is a class instance with a
 * prototype chain and a stack, `JSON.stringify` keeps neither, and every port's schema refuses one on purpose
 * (`domain/ports/schemas/ResultSchema.ts`; FE1, FE8). So the `wireResult` module copied each adapter failure into the port's shape. The
 * failure is now that shape itself — `{ code, message }`, structurally the kernel's `IEntityPortError` and deliberately not imported
 * from it (`shared/` imports nothing) — so an adapter's Result passes through a contracted port unchanged.
 */
export interface IEntityPortError {
  readonly code: string;
  readonly message: string;
}

export type IEntityFailure = {
  readonly success: false;
  readonly error: IEntityPortError;
};

export type IEntitySuccess<T> = {
  readonly success: true;
  readonly value: T;
};

export type Result<T> = IEntitySuccess<T> | IEntityFailure;

export const ok = <T>(value: T): IEntitySuccess<T> => ({
  success: true,
  value,
});

/** The code an `Error` that names none is given — as the backend's. */
const UNCLASSIFIED = 'UNCLASSIFIED';

function hasCode(error: Error | IEntityPortError): error is IEntityPortError {
  return typeof (error as Partial<IEntityPortError>).code === 'string';
}

/** A failure: a port error as it is, an `Error` as its message under `UNCLASSIFIED`. */
export const fail = (error: Error | IEntityPortError): IEntityFailure => ({
  success: false,
  error: hasCode(error) ? { code: error.code, message: error.message } : { code: UNCLASSIFIED, message: error.message },
});
